/* =========================================================================
   SAMSARA — CART-DRAWER.JS
   Clasa CartDrawer: open/close, focus trap, add-to-cart intercept,
   qty update, remove, section refresh via Section Rendering API.

   Events dispatched:
     - `cart:updated` cu detail { item_count, total_price } — pentru header
     - Ascultă `cart:open` (dispatched de header cart-trigger)

   Refresh strategy:
     - După orice modificare (add/change), fetch('/?section_id=cart-drawer')
     - Parse HTML, înlocuiește body + footer innerHTML
     - Liquid a re-randat cu money filter, empty state, shipping bar etc.
     - Zero JS-side money formatting — single source of truth Liquid.
   ========================================================================= */

(function () {
  'use strict';

  const SECTION_ID = 'cart-drawer';
  const ADD_TIMEOUT_MS = 15000; // hung socket → abort, butonul nu rămâne blocat
  const STALE_MS = 16000;       // _isAdding mai vechi de atât = self-heal (proceed)

  // GA4 event helper — no-op dacă gtag nu loaded
  function pushGA4(eventName, payload) {
    if (typeof window.dataLayer === 'undefined') return;
    window.dataLayer.push({ event: eventName, ecommerce: payload });
  }

  function ga4ItemFromShopifyLineItem(item) {
    return {
      item_id: item.sku || String(item.product_id),
      item_name: item.product_title || item.title,
      item_brand: window.Shopify && window.Shopify.shop ? window.Shopify.shop : '',
      item_variant: item.variant_title || '',
      price: (item.final_price || item.price) / 100,
      quantity: item.quantity
    };
  }

  class CartDrawer {
    constructor(drawer) {
      this.drawer = drawer;
      this.backdrop = document.querySelector('[data-cart-drawer-backdrop]');
      this.body = this.drawer.querySelector('[data-cart-body]');
      this.lastFocus = null;
      this.isRefreshing = false;
      this._isAdding = false;     // guard double-tap pe add-to-cart
      this._addStartedAt = 0;     // timestamp pentru stale self-heal
      this.smallBagsThreshold = parseInt(this.drawer.dataset.smallBagsThreshold, 10) || 3;
      // Initial state din Liquid — evit pulse animation la page load
      this.wasShippingAchieved = !!this.drawer.querySelector(
        '[data-shipping-bar].shipping-bar--achieved'
      );

      this.bindEvents();
    }

    /* ----- Weight detection (grams per item) -----
       Cascade (ALINIAT cu snippets/item-weight.liquid):
         1. item.grams — Shopify native weight (cart.js API expune valoarea
            normalizată din Admin → Products → variant → weight). CEL MAI ROBUST.
         2. DOM data-weight-g (Liquid a setat deja din metafield/variant.grams/
            title parse — deci e o sursă de adevăr autoritară post-SSR).
         3. variant_title parsing fallback (kg / g regex).
         4. Default 250g.
    */

    getItemWeightGrams(item) {
      // 1. Shopify native cart.js grams
      if (typeof item.grams === 'number' && item.grams > 0) {
        return item.grams;
      }

      // 2. DOM data-weight-g (autoritar — Liquid a computat deja cascada)
      const el = document.querySelector(
        '[data-cart-item="' + CSS.escape(item.key) + '"]'
      );
      if (el && el.dataset.weightG) {
        const parsed = parseInt(el.dataset.weightG, 10);
        if (parsed > 0) return parsed;
      }

      // 3. variant_title parsing fallback (când cart-ul nu a fost re-randat încă)
      const title = (item.variant_title || item.title || '').toLowerCase();

      const kgMatch = title.match(/(\d+(?:[.,]\d+)?)\s*kg/);
      if (kgMatch) {
        return Math.round(parseFloat(kgMatch[1].replace(',', '.')) * 1000);
      }

      const gMatch = title.match(/(\d+)\s*g(?!ra)/);
      if (gMatch) {
        return parseInt(gMatch[1], 10);
      }

      return 250;
    }

    /* ----- Free shipping status calculator -----
       Regulă Samsara: 3 pungi mici (≤500g) SAU 1 pungă ≥1kg.
       Returns {
         isFree, bagsSmall, bagsLarge, smallNeeded, progress
       }
    */

    checkFreeShipping(cart) {
      let bagsSmall = 0;
      let bagsLarge = 0;
      let hasAccessory = false;

      (cart.items || []).forEach((item) => {
        if (!this.isCoffeeItem(item)) {
          hasAccessory = true;
          return;
        }
        const weightG = this.getItemWeightGrams(item);
        const qty = item.quantity;
        if (weightG >= 1000) {
          bagsLarge += qty;
        } else {
          bagsSmall += qty;
        }
      });

      const isFree = bagsLarge >= 1 || bagsSmall >= this.smallBagsThreshold;
      const smallNeeded = Math.max(0, this.smallBagsThreshold - bagsSmall);
      const progress = isFree
        ? 100
        : Math.min(100, Math.round((bagsSmall / this.smallBagsThreshold) * 100));

      return { isFree, bagsSmall, bagsLarge, smallNeeded, progress, hasAccessory };
    }

    /* ----- Sync shipping bar UI cu cart state -----
       Rulat după refresh() pentru a:
       - Override Liquid output cu JS calcul (single source of truth JS după first load)
       - Trigger pulse animation pe transition "not-achieved" → "achieved"
    */

    updateShippingBar(cart) {
      const bar = this.drawer.querySelector('[data-shipping-bar]');
      if (!bar) {
        this.wasShippingAchieved = false;
        return;
      }

      const status = this.checkFreeShipping(cart);
      const fill = bar.querySelector('[data-shipping-bar-fill]');
      const text = bar.querySelector('[data-shipping-bar-text]');

      if (fill) {
        fill.style.width = status.progress + '%';
      }

      if (text) {
        if (status.isFree) {
          text.textContent = this.getLocale('achieved') || 'Livrare gratuită prin Easybox inclusă ✓';
        } else if (status.smallNeeded === 1) {
          text.innerHTML =
            this.getLocale('one_left_html') ||
            'Încă <strong>1 cafea</strong> pentru livrare gratuită prin Easybox';
        } else if (status.smallNeeded === 2) {
          text.innerHTML =
            this.getLocale('two_left_html') ||
            'Încă <strong>2 cafele</strong> pentru livrare gratuită prin Easybox';
        } else {
          text.innerHTML =
            this.getLocale('start_html') ||
            '<strong>3 cafele</strong> sau <strong>1 kg</strong> pentru livrare gratuită prin Easybox';
        }
      }

      const note = bar.querySelector('[data-shipping-accessory-note]');
      if (note) {
        note.textContent = this.getLocale('accessory_note') || 'Accesoriile au transport separat, afișat la checkout.';
        note.style.display = status.hasAccessory ? '' : 'none';
      }

      // Toggle achieved class + pulse pe tranziție (nu re-pulseaza dacă e deja achieved)
      const justAchieved = status.isFree && !this.wasShippingAchieved;
      bar.classList.toggle('shipping-bar--achieved', status.isFree);

      if (justAchieved) {
        bar.classList.remove('shipping-bar--pulse');
        // Force reflow ca să resetăm animația
        void bar.offsetWidth;
        bar.classList.add('shipping-bar--pulse');

        // Cleanup după animația CSS (1500ms + 100ms buffer) ca să nu rămână
        // clasa orfană în DOM după primul fire.
        if (this._pulseTimeout) clearTimeout(this._pulseTimeout);
        this._pulseTimeout = setTimeout(() => {
          bar.classList.remove('shipping-bar--pulse');
          this._pulseTimeout = null;
        }, 1600);
      }

      this.wasShippingAchieved = status.isFree;
    }

    /* ----- Localization cache (citesc de pe DOM la nevoie) ----- */

    getLocale(key) {
      // Liquid pune textele pe drawer ca data-locale-* (o singura sursa:
      // locales/ro.default.json). Fallback-urile hardcodate raman doar
      // pentru cazul in care atributul lipseste.
      const prop = 'locale' + key.split('_').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join('');
      const v = this.drawer.dataset[prop];
      return v && v.trim() ? v : null;
    }

    /* Cafea vs accesoriu. cart.js NU expune tag-urile, iar product_type e gol
       pe unele accesorii — deci DOM-ul (Liquid a citit tag-ul `brew`) e
       autoritar; product_type e doar rezerva. */
    isCoffeeItem(item) {
      const el = document.querySelector('[data-cart-item="' + CSS.escape(item.key) + '"]');
      if (el && el.dataset.isCoffee !== undefined) return el.dataset.isCoffee === '1';
      return item.product_type === 'Coffee';
    }

    /* ----- Events ----- */

    bindEvents() {
      document.addEventListener('cart:open', () => this.open());

      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-cart-close]')) {
          this.close();
        }
      });

      if (this.backdrop) {
        this.backdrop.addEventListener('click', () => this.close());
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
        if (e.key === 'Tab' && this.isOpen()) {
          this.trapFocus(e);
        }
      });

      // Intercept DOAR form-urile reale de add-to-cart. Match strict:
      // action se termină în /cart/add (routes.cart_add_url) SAU conține
      // /cart/add.js, ȘI are input[name="id"] — ca să nu prindem search/
      // newsletter/3rd-party care doar conțin substring-ul.
      document.addEventListener('submit', (e) => {
        const form = e.target;
        if (!form || typeof form.matches !== 'function') return;
        const isAddForm =
          form.matches('form[action$="/cart/add"], form[action*="/cart/add.js"]') &&
          /* `id` = formularul clasic cu o singura varianta.
             `items[` = formular cu mai multe linii (trioul saptamanii, 2026-09-10).
             Ambele merg pe aceeasi cale testata: add + refresh sectiune + deschide drawer. */
          (form.querySelector('input[name="id"]') ||
            form.querySelector('input[name^="items["]'));
        if (!isAddForm) return;
        // Drawer absent (template fără header-group) → lăsăm submit nativ → /cart
        if (!this.drawer || !this.body) return;
        e.preventDefault();
        const btn =
          e.submitter ||
          form.querySelector('[type="submit"], button:not([type])');
        this.addToCart(form, btn);
      });

      // Delegated handlers pentru qty / remove / checkout (rezistă la re-render)
      this.drawer.addEventListener('click', (e) => {
        const minus = e.target.closest('[data-quantity-minus]');
        if (minus) {
          e.preventDefault();
          const item = minus.closest('[data-cart-item]');
          if (item) this.changeQuantity(item, -1);
          return;
        }
        const plus = e.target.closest('[data-quantity-plus]');
        if (plus) {
          e.preventDefault();
          const item = plus.closest('[data-cart-item]');
          if (item) this.changeQuantity(item, +1);
          return;
        }
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) {
          e.preventDefault();
          const item = remove.closest('[data-cart-item]');
          if (item) this.removeItem(item);
          return;
        }
        const checkout = e.target.closest('[data-cart-checkout]');
        if (checkout) this.fireBeginCheckout();
      });

      // Cart note sync — debounced /cart/update.js call
      this.drawer.addEventListener('input', (e) => {
        const noteInput = e.target.closest('[data-cart-note-input]');
        if (noteInput) this.scheduleNoteSave(noteInput);
      });
    }

    /* ----- Cart note sync ----- */

    scheduleNoteSave(input) {
      if (this._noteTimer) clearTimeout(this._noteTimer);
      const status = this.drawer.querySelector('[data-cart-note-status]');
      if (status) status.textContent = '';
      this._noteTimer = setTimeout(() => this.saveNote(input), 800);
    }

    async saveNote(input) {
      const status = this.drawer.querySelector('[data-cart-note-status]');
      const note = input.value.trim().slice(0, 500);
      if (status) {
        status.textContent = window.theme?.t?.('cart.drawer.note_saving') || 'Salvez...';
      }
      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note }),
        });
        if (!response.ok) throw new Error('Update failed');
        if (status) {
          status.textContent = window.theme?.t?.('cart.drawer.note_saved') || 'Salvat ✓';
          setTimeout(() => { status.textContent = ''; }, 2000);
        }
      } catch (err) {
        console.error('[cart-drawer] note save failed', err);
        if (status) status.textContent = '';
      }
    }

    /* ----- Open / Close ----- */

    isOpen() {
      return this.drawer.getAttribute('aria-hidden') === 'false';
    }

    open() {
      if (this.isOpen()) return;
      this.lastFocus = document.activeElement;
      this.drawer.setAttribute('aria-hidden', 'false');
      if (this.backdrop) this.backdrop.setAttribute('aria-hidden', 'false');
      document.body.classList.add('is-scroll-locked');

      // Focus pe close button după animație
      window.setTimeout(() => {
        const closeBtn = this.drawer.querySelector('[data-cart-close]');
        if (closeBtn) closeBtn.focus();
      }, 150);
    }

    close() {
      if (!this.isOpen()) return;
      this.drawer.setAttribute('aria-hidden', 'true');
      if (this.backdrop) this.backdrop.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('is-scroll-locked');

      // Return focus la elementul care a deschis drawer-ul
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus();
      }
      this.lastFocus = null;
    }

    /* ----- Focus trap (Tab ciclic) ----- */

    trapFocus(e) {
      const focusables = this.getFocusableElements();
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    getFocusableElements() {
      const selector = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([readonly])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',');
      return Array.from(this.drawer.querySelectorAll(selector)).filter(
        (el) => el.offsetParent !== null
      );
    }

    /* ----- Cart API interactions ----- */

    async addToCart(form, btn) {
      const now = Date.now();
      // Double-tap guard + stale self-heal (sincron, ÎNAINTE de orice await)
      if (this._isAdding && now - this._addStartedAt < STALE_MS) return;
      this._isAdding = true;
      this._addStartedAt = now;
      this.setBtnState(btn, 'loading');

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), ADD_TIMEOUT_MS);

      const formData = new FormData(form);
      // FAST PATH: cerem section render în același request (Section Rendering API).
      // Guarded — degradează la refresh() dacă lipsește/e null/malformat.
      formData.append('sections', SECTION_ID);
      let sUrl = window.location.pathname || '/';
      if (sUrl.charAt(0) !== '/') sUrl = '/' + sUrl; // sections_url TREBUIE să înceapă cu '/'
      formData.append('sections_url', sUrl);

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });

        if (!response.ok) {
          // 422 sold-out = starea NORMALĂ mid-week pe roast-to-order
          const err = await response.json().catch(() => ({}));
          this.setBtnState(btn, 'error', err.description || 'Momentan indisponibil');
          return; // NU deschidem drawer-ul; finally curăță flag-ul
        }

        const data = await response.json();

        // GA4 — propriul try/catch + array-validated, ca o eroare de mapping
        // să nu blocheze niciodată drawer-ul sau restore-ul butonului.
        try {
          const addedItems = Array.isArray(data.items)
            ? data.items
            : data && data.id
            ? [data]
            : [];
          if (addedItems.length) {
            pushGA4('add_to_cart', {
              currency:
                (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
                'RON',
              value: addedItems.reduce(
                (s, i) =>
                  s +
                  (i.final_line_price || i.line_price || (i.final_price || i.price) * i.quantity) /
                    100,
                0
              ),
              items: addedItems.map(ga4ItemFromShopifyLineItem),
            });
          }
        } catch (gaErr) {
          console.warn('[cart-drawer] GA4 skip', gaErr);
        }

        // GUARDED FAST PATH: injectează HTML-ul bundled dacă există + e parseabil,
        // altfel cade pe refresh() (2 fetch-uri, full cart — updateShippingBar intact).
        const drawerHTML = data.sections && data.sections[SECTION_ID];
        let injected = false;
        if (drawerHTML) injected = this.injectDrawerHTML(drawerHTML);

        if (injected) {
          // 2026-07-17: NU mai deschidem drawer-ul automat la add (decizie UX Iustin).
          // Feedback discret în loc de fereastră care întrerupe: starea "✓ Adăugat"
          // pe buton (1.4s, setBtnState mai jos) + pulsul pe numărul din coș (header.js).
          // Drawer-ul se deschide doar la click pe iconița de coș (cart:open).
          // Header optimist: badge-ul se actualizează imediat, chiar dacă fetch-ul
          // de reconcile eșuează (nu mai depinde de al doilea /cart.js).
          const addedQty = Array.isArray(data.items)
            ? data.items.reduce((s, i) => s + (i.quantity || 0), 0)
            : (data && data.quantity) || 1;
          const countEl = document.querySelector('[data-cart-count]');
          const baseCount = countEl ? parseInt(countEl.dataset.count, 10) || 0 : 0;
          document.dispatchEvent(
            new CustomEvent('cart:updated', {
              detail: { item_count: baseCount + addedQty, total_price: null },
            })
          );
          this.reconcileShipping(); // /cart.js: shipping recompute + cart:updated autoritar
        } else {
          await this.refresh(); // fallback robust (dispatch-uiește cart:updated singur)
          // drawer-ul NU se mai deschide la add — vezi nota din fast-path de mai sus
        }

        // Închidem popup-ul DOAR pe success (dacă add-ul a venit dintr-un <dialog>).
        // Pe eroare rămâne deschis → feedback-ul din dialog e vizibil.
        if (btn && typeof btn.closest === 'function') {
          const dlg = btn.closest('dialog');
          if (dlg && typeof dlg.close === 'function' && dlg.open) dlg.close();
        }

        if (btn && btn.isConnected) {
          this.setBtnState(btn, 'added');
          // Timer per-nod (nu pe instanță): un add pe alt buton nu mai anulează
          // reset-ul ăstuia → nu rămâne blocat în is-add-added{pointer-events:none}.
          if (btn._samsaraAddedTimer) clearTimeout(btn._samsaraAddedTimer);
          btn._samsaraAddedTimer = setTimeout(() => this.setBtnState(btn, 'idle'), 1400);
        }
      } catch (err) {
        const msg =
          err && err.name === 'AbortError'
            ? 'Conexiune lentă, reîncearcă'
            : 'Eroare de rețea. Reîncearcă.';
        this.setBtnState(btn, 'error', msg);
        console.error('[cart-drawer] add error:', err);
      } finally {
        clearTimeout(timeoutId);
        this._isAdding = false; // SINGUR loc de clearing: success / 422 / reject / abort / throw
      }
    }

    /* ----- Button feedback state (loading / added / error) -----
       Aplicat pe butonul capturat (e.submitter). Clase base-agnostice fiindcă
       butonul e uneori .btn.btn-primary, alteori .coffee-card__add-link. */

    setBtnState(btn, state, msg) {
      if (!btn || !btn.isConnected) {
        // Popup-ul își detașează butonul la 50ms — surface eroarea altfel
        if (state === 'error') {
          document.dispatchEvent(new CustomEvent('cart:add-error', { detail: { msg: msg } }));
        }
        return;
      }
      btn.classList.remove('is-add-loading', 'is-add-added', 'is-add-error');

      if (state === 'loading') {
        btn.classList.add('is-add-loading');
        btn.setAttribute('aria-busy', 'true');
        btn.setAttribute('aria-disabled', 'true');
        return;
      }
      if (state === 'added') {
        btn.classList.add('is-add-added');
        btn.removeAttribute('aria-busy');
        btn.removeAttribute('aria-disabled');
        return;
      }
      if (state === 'error') {
        btn.classList.add('is-add-error');
        btn.removeAttribute('aria-busy');
        btn.removeAttribute('aria-disabled'); // re-enable: sold-out e normal, userul poate reîncerca
        const fb = this.getFeedbackEl(btn);
        if (fb) {
          fb.textContent = msg || 'Eroare. Reîncearcă.';
          fb.hidden = false;
        }
        if (btn._samsaraErrorTimer) clearTimeout(btn._samsaraErrorTimer);
        btn._samsaraErrorTimer = setTimeout(() => {
          if (btn.isConnected) btn.classList.remove('is-add-error');
          if (fb) {
            fb.hidden = true;
            fb.textContent = '';
          }
        }, 4000);
        return;
      }
      // idle
      btn.removeAttribute('aria-busy');
      btn.removeAttribute('aria-disabled');
      const fb = this.getFeedbackEl(btn);
      if (fb) {
        fb.hidden = true;
        fb.textContent = '';
      }
    }

    getFeedbackEl(btn) {
      if (!btn || !btn.isConnected || !btn.parentElement) return null;
      let fb = btn.parentElement.querySelector('.btn-feedback');
      if (!fb) {
        fb = document.createElement('p');
        fb.className = 'btn-feedback';
        fb.setAttribute('role', 'status');
        fb.setAttribute('aria-live', 'polite');
        fb.hidden = true;
        btn.insertAdjacentElement('afterend', fb);
      }
      return fb;
    }

    /* ----- Fast-path DOM inject (din sections['cart-drawer']) -----
       Întoarce true DOAR dacă body-ul a fost comis; altfel false → caller
       cade pe refresh(). NU șterge footer-ul (checkout CTA) pe parse glitch. */

    injectDrawerHTML(htmlString) {
      try {
        const doc = new DOMParser().parseFromString(htmlString, 'text/html');
        const newBody = doc.querySelector('[data-cart-body]');
        const newFooter = doc.querySelector('[data-cart-footer]');
        const oldFooter = this.drawer.querySelector('[data-cart-footer]');
        if (!newBody || !this.body) return false; // parse glitch → fallback
        this.body.innerHTML = newBody.innerHTML;
        if (newFooter) {
          if (oldFooter) {
            oldFooter.innerHTML = newFooter.innerHTML;
          } else {
            this.drawer.appendChild(newFooter.cloneNode(true));
          }
        }
        // newFooter lipsă: NU eliminăm oldFooter — riscă să șteargă checkout CTA.
        return true;
      } catch (e) {
        return false;
      }
    }

    /* ----- Reconcile shipping bar pe fast-path (non-blocant) -----
       Single-add response nu conține cart.items[].grams; luăm /cart.js separat
       doar pentru recalcul JS + pulse + cart:updated. Dacă eșuează, bara
       server-rendered rămâne (corectă). */

    reconcileShipping() {
      // NU resetăm wasShippingAchieved din DOM-ul injectat (deja --achieved
      // server-side) — ar suprima pulse-ul pe tranziția de atingere. Lăsăm
      // updateShippingBar să detecteze false→true din valoarea purtată între
      // operații, exact ca path-ul refresh().
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          this.updateShippingBar(cart);
          document.dispatchEvent(
            new CustomEvent('cart:updated', {
              detail: { item_count: cart.item_count, total_price: cart.total_price },
            })
          );
        })
        .catch(() => {});
    }

    async changeQuantity(itemEl, delta) {
      const input = itemEl.querySelector('[data-quantity-input]');
      if (!input) return;
      const current = parseInt(input.value, 10) || 0;
      const next = Math.max(0, current + delta);
      await this.changeItem(itemEl.dataset.cartItem, next);
    }

    fireBeginCheckout() {
      // Fire-and-forget: nu blocăm navigarea spre /checkout
      if (typeof window.dataLayer === 'undefined') return;
      try {
        fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then((r) => r.json())
          .then((cart) => {
            pushGA4('begin_checkout', {
              currency: cart.currency || 'RON',
              value: cart.total_price / 100,
              items: (cart.items || []).map(ga4ItemFromShopifyLineItem)
            });
          })
          .catch(() => {});
      } catch (e) {}
    }

    async removeItem(itemEl) {
      // GA4 remove_from_cart event — capture item data BEFORE change
      const titleEl = itemEl.querySelector('.cart-item__title');
      const priceText = (itemEl.querySelector('.cart-item__price') || {}).textContent || '';
      const qtyInput = itemEl.querySelector('[data-quantity-input]');
      const priceMatch = priceText.match(/([\d.,]+)/);
      pushGA4('remove_from_cart', {
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'RON',
        items: [{
          item_id: itemEl.dataset.variantId || itemEl.dataset.cartItem,
          item_name: titleEl ? titleEl.textContent.trim() : '',
          item_brand: window.Shopify && window.Shopify.shop ? window.Shopify.shop : '',
          quantity: qtyInput ? parseInt(qtyInput.value, 10) : 1,
          price: priceMatch ? parseFloat(priceMatch[1].replace(',', '.')) : 0
        }]
      });
      await this.changeItem(itemEl.dataset.cartItem, 0);
    }

    async changeItem(key, quantity) {
      if (this.isRefreshing) return;
      this.isRefreshing = true;
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ id: key, quantity: quantity }),
        });
        if (!response.ok) throw new Error('Change failed');
        const cart = await response.json();
        await this.refresh(cart);
      } catch (err) {
        console.error('[cart-drawer] change error:', err);
      } finally {
        this.isRefreshing = false;
      }
    }

    /* ----- Refresh prin Section Rendering API ----- */

    async refresh(cart) {
      // Parallelize cart JSON (dacă nu e dat) + section HTML — nu serial.
      try {
        const cartPromise = cart
          ? Promise.resolve(cart)
          : fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        const htmlPromise = fetch('/?section_id=' + SECTION_ID).then((r) => r.text());
        const [resolvedCart, html] = await Promise.all([cartPromise, htmlPromise]);
        cart = resolvedCart;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newBody = doc.querySelector('[data-cart-body]');
        const newFooter = doc.querySelector('[data-cart-footer]');
        const oldFooter = this.drawer.querySelector('[data-cart-footer]');

        if (newBody && this.body) {
          this.body.innerHTML = newBody.innerHTML;
        }

        if (newFooter) {
          if (oldFooter) {
            oldFooter.innerHTML = newFooter.innerHTML;
          } else {
            this.drawer.appendChild(newFooter.cloneNode(true));
          }
        } else if (oldFooter) {
          oldFooter.remove();
        }
      } catch (err) {
        console.error('[cart-drawer] refresh failed', err);
        return;
      }

      // După refresh DOM, sincronizăm shipping bar cu cart state +
      // trigger pulse pe tranziția achievement
      this.updateShippingBar(cart);

      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: {
            item_count: cart.item_count,
            total_price: cart.total_price,
          },
        })
      );
    }
  }

  /* --- Bootstrap --- */

  function boot() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    window.SamsaraCart = new CartDrawer(drawer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
